import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../store/store";
import {
  createTSFolder,
  syncFiles,
  clearMessages,
} from "../../store/admin/createAndSyncSlice";
import { useAppSelector } from "../../store/hooks";
import { useToast } from "../utilities/Toast";

interface UploadModalProps {
  caseId: string;
  boxFolderId: string;
  userId: string; // added userId
  onClose: () => void;
}

const BOX_INVALID_NAME_CHARS = /[\/\\:*?"<>|\x00-\x1f\x7f]/;

const validatePatientFolderNames = (raw: string): string | null => {
  const names = raw
    .split(",")
    .map((name) => name.trim())
    .filter(Boolean);

  if (!names.length) return "Folder name cannot be empty";

  for (const name of names) {
    if (name === "." || name === "..") {
      return 'Folder name cannot be "." or ".."';
    }
    if (BOX_INVALID_NAME_CHARS.test(name)) {
      return 'Folder name contains invalid characters: / \\ : * ? " < > |';
    }
  }

  return null;
};

const UploadModal: React.FC<UploadModalProps> = ({
  caseId,
  boxFolderId,
  userId,
  onClose,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { showToast } = useToast();
  const { user } = useAppSelector((state) => state.auth)
  const { employee } = useAppSelector((state) => state.allocationAuth)
  const { tsMessage, syncMessage, error } = useSelector(
    (state: RootState) => state.createAndSync
  );

  const [patientFolderName, setPatientFolderName] = useState("");
  const [tsButtonState, setTsButtonState] = useState<
    "idle" | "creating" | "success" | "error"
  >("idle");
  const [syncButtonState, setSyncButtonState] = useState<
    "idle" | "syncing" | "success" | "error"
  >("idle");

  // TS Folder messages
  useEffect(() => {
    if (tsMessage) {
      setTsButtonState("success");
      const timeout = setTimeout(() => setTsButtonState("idle"), 3000);
      return () => clearTimeout(timeout);
    }
  }, [tsMessage]);

  // Sync messages
  useEffect(() => {
    if (syncMessage) {
      setSyncButtonState("success");
      const timeout = setTimeout(() => setSyncButtonState("idle"), 3000);
      return () => clearTimeout(timeout);
    }
  }, [syncMessage]);

  // Errors
  useEffect(() => {
    if (error) {
      if (tsButtonState === "creating") setTsButtonState("error");
      if (syncButtonState === "syncing") setSyncButtonState("error");

      const timeout = setTimeout(() => {
        setTsButtonState("idle");
        setSyncButtonState("idle");
      }, 3000);
      return () => clearTimeout(timeout);
    }
  }, [error]);

  const handleBackgroundClick = () => {
    dispatch(clearMessages());
    onClose();
  };

  const handleModalClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  const handleMakeTSFolder = async () => {
    const trimmedName = patientFolderName.trim();
    if (!trimmedName) return;

    const validationError = validatePatientFolderNames(trimmedName);
    if (validationError) {
      setTsButtonState("error");
      showToast(validationError, "error");
      setTimeout(() => setTsButtonState("idle"), 3000);
      return;
    }

    setTsButtonState("creating");
    const result = await dispatch(
      createTSFolder({ boxFolderId, patientFolderName: trimmedName, caseId }),
    );

    if (createTSFolder.fulfilled.match(result)) {
      showToast(result.payload || "TS folder created", "success");
    } else if (createTSFolder.rejected.match(result)) {
      showToast(
        (result.payload as string) || "Failed to create TS folder",
        "error",
      );
    }
  };

  const handleSyncFiles = async () => {
    setSyncButtonState("syncing");
    const result = await dispatch(
      syncFiles({
        caseId,
        boxFolderId,
        userId,
        uploadBy: employee?.emp_name || user?.user_name || "Staff",
      }),
    );

    if (syncFiles.fulfilled.match(result)) {
      showToast(result.payload.message || "Files synced", "success");
    } else if (syncFiles.rejected.match(result)) {
      showToast((result.payload as string) || "Failed to sync files", "error");
    }
  };

  const getTSButtonText = () => {
    switch (tsButtonState) {
      case "idle":
        return "Make TS_Folder";
      case "creating":
        return "Creating...";
      case "success":
        return "Created!";
      case "error":
        return "Error";
    }
  };

  const getSyncButtonText = () => {
    switch (syncButtonState) {
      case "idle":
        return "Sync Files";
      case "syncing":
        return "Syncing...";
      case "success":
        return "Synced!";
      case "error":
        return "Error";
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={handleBackgroundClick}
    >
      <div
        className="bg-white p-6 rounded shadow-lg w-80 relative"
        onClick={handleModalClick}
      >
        {/* Cross button */}
        <button
          onClick={handleBackgroundClick}
          className="absolute top-2 right-2 text-gray-600 hover:text-gray-900 font-bold text-xl"
        >
          &times;
        </button>

        <h2 className="text-lg font-semibold mb-4">
          Upload Options for {caseId}
        </h2>

        <div className="flex flex-col gap-2">
          <input
            type="text"
            value={patientFolderName}
            onChange={(e) => setPatientFolderName(e.target.value)}
            placeholder="Enter patient folder name"
            className="border px-3 py-2 rounded"
          />

          <button
            onClick={handleMakeTSFolder}
            className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 disabled:opacity-50"
            disabled={tsButtonState === "creating" || !patientFolderName.trim()}
          >
            {getTSButtonText()}
          </button>

          <button
            onClick={handleSyncFiles}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50"
            disabled={syncButtonState === "syncing"}
          >
            {getSyncButtonText()}
          </button>
        </div>
      </div>
    </div>
  );
};

export default UploadModal;
